import { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { getAllContentUris } from '../components/lib/wp';
import { getAuthString } from '../components/utils/getAuthString';
import { getCompiledQuery } from '../components/utils/compileQuery.wp';
import { transformData } from '../components/utils/compileTransforms.wp';
import { gql } from '@apollo/client';
import client from '../components/lib/apolloClient';
import BlockLayout from '../components/layout/BlockLayout';
import HeadViewport from '../components/fragments/head-viewport/HeadViewport';
import HeadIcons from '../components/fragments/head-icons/HeadIcons';
import Yoast from '../components/seo/yoast/Yoast';

import PageTransition from '../components/fragments/page-transition/PageTransition';

import { getGravityForm } from 'next-gravity-forms/server';
import { loadCardsQuery } from '../components/utils/LoadCards.query.wp';
import {
  transformLoadCards,
  getTotalPages,
} from '../components/utils/LoadCards.transform.wp';
import {
  buildCardVariables,
  CARD_GRID_PAGE_SIZE,
  CARD_GRID_PREFETCH_ALL_LIMIT,
} from '../components/utils/useLoadCards';
import { DEEP_DIVE_POST_TYPES } from '../components/blocks/card-grid/CardGrid.transform.wp';
import {
  filterGroupsQuery,
  buildFilterGroups,
} from '../components/utils/FilterGroups.query.wp';

const gravityFormSaveAndContinueQuery = gql`
  query GetGravityFormSaveAndContinue($id: ID!) {
    gfForm(id: $id, idType: DATABASE_ID) {
      databaseId
      saveAndContinue {
        hasSaveAndContinue
        buttonText
      }
    }
  }
`;

const normalizeGravityFormId = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

const footerGravityFormIdQuery = gql`
  query GetFooterGravityFormId {
    globalOptionsDefault {
      globalNavigationfooter {
        global_navigationfooter_gravity_form_id
      }
    }
  }
`;

function Page({ data, gravityForms, globalData, isInitialLoad }) {
  if (data) {
    // console.log("pageData", data.pageData);
    return (
      <>
        <HeadViewport />
        <HeadIcons />
        <Yoast seo={data?.page?.seo} />

        {/* <PageTransition theme={data?.pageData?.colorTheme}> */}
        <BlockLayout
          blocks={data.blocks}
          pageData={data.pageData}
          postType={data.postType}
          globalData={globalData}
          isInitialLoad={isInitialLoad}
          gravityForms={gravityForms}
        />
        {/* </PageTransition> */}
      </>
    );
  } else {
    return null;
  }
}

export async function getStaticProps({ params }) {
  const slugSegments = (params.slug || []).filter(Boolean);
  const slug = '/' + slugSegments.join('/');

  // CMS preview links land on /preview/{databaseId} or /{type}/preview/{databaseId}
  // (see pages/api/preview.js), so detect preview by URL pattern.
  const [firstSegment, secondSegment, thirdSegment] = slugSegments;

  const isPreviewWithNumericId =
    firstSegment === 'preview' && secondSegment && /^\d+$/.test(secondSegment);
  const isPostTypeWithPreviewAndNumericId =
    secondSegment === 'preview' && thirdSegment && /^\d+$/.test(thirdSegment);

  const isPreview = isPreviewWithNumericId || isPostTypeWithPreviewAndNumericId;

  let postType;
  let postId;

  if (isPreview) {
    postId = isPostTypeWithPreviewAndNumericId ? thirdSegment : secondSegment;

    const typeFromUrl = isPostTypeWithPreviewAndNumericId
      ? firstSegment.toLowerCase()
      : '';

    postType =
      typeFromUrl === 'articles'
        ? 'News'
        : typeFromUrl === 'events' || typeFromUrl === 'event'
          ? 'Event'
          : 'Page';
  } else {
    // Don't set postType for index pages (home or section indexes like /articles or /events)
    const isIndexPage = slugSegments.length === 0 || slugSegments.length === 1;

    postType = isIndexPage
      ? 'Page'
      : slugSegments[0] === 'articles'
        ? 'News'
        : slugSegments[0] === 'events'
          ? 'Event'
          : 'Page';
  }

  const compiledQuery = getCompiledQuery(postType, isPreview);

  // console.log('compiledQuery', compiledQuery);

  try {
    const { data, errors } = await client.query({
      query: gql`
        ${compiledQuery}
      `,
      // The identifier is always a variable — a URI for normal renders, a
      // database ID for previews (see getCompiledQuery).
      variables: { id: isPreview ? postId : slug },
      ...(isPreview && {
        context: {
          headers: {
            Authorization: getAuthString(),
          },
        },
      }),
      fetchPolicy: 'no-cache',
    });

    // WPGraphQL often returns partial-data errors (null non-nullable fields,
    // schema drift on one block) alongside perfectly renderable content. If a
    // throw happened here during ISR revalidation, Next would silently serve
    // the stale page forever — so only treat errors as fatal when no content
    // came back at all; otherwise log and render from the partial data.
    if (errors?.length) {
      const message = `GraphQL error fetching ${slug || '/'}: ${errors.map((e) => e.message).join('; ')}`;

      if (!data?.contentNode) {
        // No content AND errors — a backend failure, not a genuine 404. Throw
        // so the outer catch surfaces it instead of caching a false 404.
        throw new Error(message);
      }

      console.error(`[partial GraphQL errors, rendering anyway] ${message}`);
    }

    // Check if contentNode existed (page was found)
    if (!data?.contentNode) {
      // WPGraphQL resolved the query with no matching content — a genuine 404.
      return { notFound: true, revalidate: 1 };
    }

    const transformedData = transformData(data);

    // NEW: Fetch cards for CardCarousel/CardGrid blocks
    const blocks = transformedData?.blocks || [];

    const cardBlocks = blocks.filter((block) => {
      const cardCarouselfetch =
        block?.type === 'card-carousel' &&
        block?.source === 'postType' &&
        (!block?.cards || block.cards.length === 0);

      return cardCarouselfetch || block?.type === 'card-grid';
    });

    // The filterable grid is paginated: prefetch page 1 at the grid page size.
    // People grids (staff, board members) are the exception — they prefetch
    // the whole set and paginate client-side, so every name is in the static
    // HTML. Pages 2+ only exist after a click, so Google never saw anyone past
    // the first page and name searches ("<name> TQS") stopped finding them.
    cardBlocks.forEach((block) => {
      if (block.type !== 'card-grid') return;
      block.prefetchedAll = DEEP_DIVE_POST_TYPES.includes(block.sourcePostType);
      block.numPosts = block.prefetchedAll
        ? CARD_GRID_PREFETCH_ALL_LIMIT
        : CARD_GRID_PAGE_SIZE;
    });

    // Taxonomy filter options are page-wide, not per-block, so fetch them once
    // and reuse across every card-grid block instead of refetching per block.
    const needsFilterGroups = cardBlocks.some(
      (block) => block.type === 'card-grid'
    );
    const filterGroupsPromise = needsFilterGroups
      ? client
          .query({ query: filterGroupsQuery, fetchPolicy: 'no-cache' })
          .then(({ data }) => data)
          .catch((error) => {
            console.error('Error fetching filter groups:', error);
            return null;
          })
      : null;

    // The footer's sign-up Gravity Form is a global option, not a page block,
    // but the footer renders on every page — so it rides along with this
    // page's own Gravity Form fetch loop below.
    const footerGravityFormIdPromise = client
      .query({ query: footerGravityFormIdQuery, fetchPolicy: 'no-cache' })
      .then(({ data }) => data)
      .catch((error) => {
        console.error('Error fetching footer gravity form id:', error);
        return null;
      });

    // Fetch every block's cards concurrently instead of one round-trip at a time.
    await Promise.all(
      cardBlocks.map(async (block) => {
        // Build variables with the same logic the client-side hook uses,
        // so the pre-fetched cards match what useLoadCards would resolve.
        const variables = buildCardVariables(block);

        try {
          const { data: cardsData } = await client.query({
            query: loadCardsQuery,
            variables,
            fetchPolicy: 'no-cache',
          });

          block.cards = transformLoadCards(cardsData?.cardPosts?.posts || []);
          if (block.type === 'card-grid') {
            block.totalPages = block.prefetchedAll
              ? Math.max(1, Math.ceil(block.cards.length / CARD_GRID_PAGE_SIZE))
              : getTotalPages(cardsData?.cardPosts);
          }
        } catch (error) {
          console.error('Error fetching cards for block:', error);
          block.cards = []; // Set empty array on error
          if (block.type === 'card-grid') block.totalPages = 1;
        }

        if (block.type === 'card-grid') {
          const filterData = await filterGroupsPromise;
          block.filters = filterData
            ? buildFilterGroups(filterData, block.sourcePostType)
            : [];
        }
      })
    );

    const gravityFormBlocks = blocks.filter(
      (block) => block.type === 'gravity-form'
    );
    const gravityFormIds = gravityFormBlocks
      .map((block) => normalizeGravityFormId(block.gravityFormId))
      .filter(Boolean);

    const footerGravityFormIdData = await footerGravityFormIdPromise;
    const footerGravityFormId = normalizeGravityFormId(
      footerGravityFormIdData?.globalOptionsDefault?.globalNavigationfooter
        ?.global_navigationfooter_gravity_form_id
    );
    if (footerGravityFormId) {
      gravityFormIds.push(footerGravityFormId);
    }

    // Step 3: Deduplicate form IDs
    const uniqueFormIds = Array.from(new Set(gravityFormIds));

    // Filter out undefined/null IDs before fetching
    const validFormIds = uniqueFormIds;

    if (gravityFormBlocks.length > 0 && validFormIds.length === 0) {
      console.warn(
        `⚠️ Gravity Form block has no valid selected form on page: ${slug || '/'}`
      );
    }

    // Step 4: Fetch all Gravity Forms in parallel
    const gravityForms = {};
    if (process.env.NEXT_PUBLIC_WORDPRESS_API_URL) {
      const formFetchPromises = validFormIds.map(async (formId) => {
        try {
          const parsedFormId = parseInt(formId, 10);
          const form = await getGravityForm(parsedFormId);

          try {
            const { data: saveAndContinueData } = await client.query({
              query: gravityFormSaveAndContinueQuery,
              variables: { id: parsedFormId },
              fetchPolicy: 'no-cache',
            });

            if (form?.gfForm) {
              form.gfForm.saveAndContinue =
                saveAndContinueData?.gfForm?.saveAndContinue || null;
            }
          } catch (saveAndContinueError) {
            if (process.env.NODE_ENV !== 'production') {
              console.error(
                `⚠️ Failed to fetch saveAndContinue metadata for Gravity Form ${formId}:`,
                saveAndContinueError
              );
            }
          }

          gravityForms[formId] = form;
        } catch (error) {
          console.error(`❌ Failed to fetch Gravity Form ${formId}:`, error);
          gravityForms[formId] = null;
        }
      });

      await Promise.all(formFetchPromises);
    }

    return {
      props: {
        data: {
          ...transformedData,
          postType: postType,
        },
        gravityForms: gravityForms,
        isDraftModeEnabled: isPreview,
        // The resolved path for this page. `router.asPath` is not the resolved
        // URL while Vercel server-renders a statically generated page, so
        // anything that affects markup must key off this instead (see _app.js).
        routePath: slug,
      },
      // Don't cache preview renders; cache normal pages for 60s
      revalidate: isPreview ? 1 : 60,
    };
  } catch (error) {
    console.error(`Error building page ${slug || '/'}:`, error);
    // Rethrow infra/GraphQL errors instead of caching a false 404: during ISR
    // revalidation Next keeps serving the last known-good page and retries on
    // the next window; only a genuine "no matching content" result above
    // should ever produce a 404.
    throw error;
  }
}

export async function getStaticPaths() {
  const allUris = await getAllContentUris();
  const paths = allUris.map((slug) => ({ params: { slug } }));

  // Add homepage explicitly to prevent 404 caching
  paths.unshift({ params: { slug: [] } });

  return {
    paths,
    fallback: 'blocking', // On-demand ISR for new pages
  };
}

export default Page;
